import { Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Field type do Formly para listas de valores escalares (number/string).
 * Renderiza cada valor como um chip removível (hlmBadge) e um input para
 * adicionar novos (Enter confirma). Quando props.numeric é true, os valores
 * são convertidos para number antes de entrar no array (ex.: branchCodeList).
 */
@Component({
  selector: 'evolve-chips-list',
  imports: [ReactiveFormsModule, HlmBadge, NgIcon, TranslatePipe],
  template: `
    @if (props.label) {
      <label
        class="block text-sm font-medium text-foreground mb-1"
      >
        {{ props.label }}
      </label>
    }
    <div
      class="flex flex-wrap items-center gap-2 rounded-md border border-border p-2"
    >
      @for (item of items; track $index) {
        <span hlmBadge variant="secondary" class="gap-1">
          {{ display(item) }}
          <button
            type="button"
            class="hover:text-destructive"
            (click)="removeAt($index)"
            [attr.aria-label]="'shared.forms.chipsList.remove' | translate"
          >
            <ng-icon name="lucideX" class="text-xs" />
          </button>
        </span>
      }
      <input
        type="text"
        [placeholder]="
          props.placeholder ?? ('shared.forms.chipsList.addPlaceholder' | translate)
        "
        class="flex-1 min-w-24 border-0 bg-transparent text-sm outline-none focus:ring-0"
        (keydown.enter)="add($event)"
      />
    </div>
    @if (props.description) {
      <small class="text-muted-foreground">{{ props.description }}</small>
    }
  `,
})
export class ChipsListFieldComponent extends FieldType<FieldTypeConfig> {
  // Getter (não signal): os handlers de evento rodam na zona do Angular e disparam CD,
  // re-lendo este getter — suficiente para refletir add/remove e a pré-população.
  get items(): unknown[] {
    return (this.formControl.value as unknown[] | null) ?? [];
  }

  private get numeric(): boolean {
    return (this.props as Record<string, unknown>)['numeric'] === true;
  }

  display(item: unknown): string {
    return String(item);
  }

  add(event: Event): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const raw = input.value.trim();
    if (!raw) return;

    let value: unknown = raw;
    if (this.numeric) {
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) return;
      value = parsed;
    }

    this.setValue([...this.items, value]);
    input.value = '';
  }

  removeAt(index: number): void {
    const next = [...this.items];
    next.splice(index, 1);
    this.setValue(next);
  }

  private setValue(next: unknown[]): void {
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }
}
